import React from "react";
import { useDrag, useDrop } from "react-dnd";
import { Card, Label } from "semantic-ui-react";
import { convertToDollar, convertToPercentString } from "../../Helpers";

const style = {
  marginBottom: "10px",
  cursor: "move",
};

export interface CardProps {
  id: string;
  index: number;
  card: any;
  category: number;
  cards: any;
  moveCard: (card: any, draggedIndex: number, overIndex: number, draggedCategoryIndex: number, overCategoryIndex: number) => void;
  findCard: (id: string, category: number) => { index: number };
  handleCard: any;
}

const LOKanbanCard: React.FC<CardProps> = ({ id, index, card, category, cards, moveCard, findCard, handleCard }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const originalIndex = findCard(id, category).index;
  const [{ isDragging }, drag] = useDrag({
    item: { type: "card", id, originalIndex },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: () => void 0,
  });

  const [, drop] = useDrop({
    accept: "card",
    canDrop: () => false,
    hover(item: any, monitor: any) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.originalIndex;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as any).y - hoverBoundingRect.top;

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      const draggedCategory = cards.find(x => {
        return x.cards.find(y => y.id === item.id);
      });
      const draggedCard = draggedCategory.cards.find(x => x.id === item.id);
      const draggedIndex = draggedCategory.cards.indexOf(draggedCard);
      moveCard(draggedCard, draggedIndex, hoverIndex, draggedCategory.index, category);
      item.originalIndex = hoverIndex;
    },
  });

  const calculateDaysLeft = (date) => {
    const today = new Date();
    const dueDate = new Date(date);
    const timeDiff = dueDate.getTime() - today.getTime(); 
    const dayDiff = timeDiff / (1000 * 3600 * 24);
    return Math.round(dayDiff);
  };

  const opacity = isDragging ? 0 : 1;
  drag(drop(ref));
  return (
    <div ref={ref} style={{ ...style, opacity }}>
      <Card className="kanban-card kanban-card-grow" onClick={() => handleCard(card.id)}>
        <Card.Content className="kanban-card-content">
          <Card.Header className="kanban-card-header">
            <div>{card.name}</div>
            <div className="kanban-card-meta">{card.address}</div>
          </Card.Header>
          <Card.Description className="kanban-card-description">
            <div>Loan Amount: <b>{convertToDollar(card.loanAmount)}</b></div>
            <div>
              <span>Closing: <b>{card.closing}</b> </span>
              <span className={calculateDaysLeft(card.closing) > 0 ? "kanban-green-date" : 
                calculateDaysLeft(card.closing) === 0 ? "kanban-orange-date" :
                "kanban-red-date"}>({calculateDaysLeft(card.closing)} Days)</span>
            </div>
            <div>Completeness: <b>{convertToPercentString(card.completeness)}</b></div>
            <div>
              {card.tags.map((item, i) => {
                return (
                  <Label key={"card-tag-" + i} color={item.type === "tc" ? "yellow" : item.type === "lo" ? "orange" : "grey"} size={"tiny"}>
                    {item.value}
                    {(item.type === "tc" || item.type === "lo") && <Label.Detail>{item.type.toUpperCase()}</Label.Detail>}
                  </Label>
                );
              })}
            </div>
          </Card.Description>
        </Card.Content>
      </Card>
    </div>
  );
};

export default LOKanbanCard;